import { CreateInvoicePayload, WaterInvoiceRepo } from "@/core/repos/tables/water-invoice.repo";
import container from "@/lib/di/container";
import { Alert, Button, Modal, Form, message, DatePicker, InputNumber, Select, ConfigProvider, Popconfirm, Tooltip } from "antd";
import { useState, useEffect, useRef, Dispatch, SetStateAction } from "react";
import { InvoiceTable } from "./InvoiceModal";
import { dayjsPipe } from "@/core/pipes/dayjs.pipe";
import { ContractModel } from "@/core/models/contracts/contract.model";
import { ContractRepo } from "@/core/repos/tables/contract.repo";
import { useDebounce } from "use-debounce";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { CloseOutlined, ExclamationCircleFilled, EyeOutlined } from "@ant-design/icons";
import { WaterIndexRepo } from "@/core/repos/tables/water-index.repo";
import { AppUserModel } from "@/core/models/app-user.model";
import { DEF_DATE_FORMAT, DEF_MONTH_FORMAT } from "@/app/home/utils/global.utils";
import { getInvoiceSavePayload, initialInvoicePreview, invoicePreviewReducer, InvoicePreviewAction } from "./invoice-preview";

const waterInvoiceRepo = container.get(WaterInvoiceRepo);
const contractRepo = container.get(ContractRepo);
const waterIndexRepo = container.get(WaterIndexRepo);

interface CreateInvoiceModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
}
export const CreateInvoiceModal: React.FC<CreateInvoiceModalProps> = ({isModalOpen, setIsModalOpen}) => {
    const [loading, setLoading] = useState(false);
    const [preview, setPreview] = useState(initialInvoicePreview);
    const previewRef = useRef(initialInvoicePreview);
    const savingRef = useRef(false);
    const [previewError, setPreviewError] = useState<string>();
    const data = preview.invoice;
    const invoicePayload = getInvoiceSavePayload(preview);
    const previewLoading = preview.loading;
    const updatePreview = (action: InvoicePreviewAction) => {
        const next = invoicePreviewReducer(previewRef.current, action);
        previewRef.current = next;
        setPreview(next);
        return next;
    };
    const resetPreview = () => {
        updatePreview({ type: 'invalidate' });
        setPreviewError(undefined);
    };
    const [month, setMonth] = useState<string>();
    const [year, setYear] = useState<string>();
    const [waterUserId, setWaterUserId] = useState<string>();
    const [isManual, setManual] = useState<boolean>(false);
    const [form] = Form.useForm();
    
    const [appUsers] = useState<AppUserModel[]>([]);
    
    const [userOptions, setUserOptions] = useState<ContractModel[]>([]);
    const [userSearchQuery, setUserSearchQuery] = useState<string>("");
    const [debouncedUserSearchQuery] = useDebounce(userSearchQuery, 200);

    const fetchUsers = async (search?: string) => {
        const users = await contractRepo.getData(1, {search}, 20);
        if (users) {
            setUserOptions(users.data);
        }
    }

    // Initialize select options data
    const init = async () => {
        await fetchUsers();
    }

    // componentDidMount equivalent, run exclusively on client-side
    useEffect(() => {init()}, []);

    useEffect(() => {
        fetchUsers(debouncedUserSearchQuery);
    }, [debouncedUserSearchQuery]);

    useEffect(() => {
        if (!isModalOpen) {
            previewRef.current = invoicePreviewReducer(previewRef.current, { type: 'invalidate' });
            setPreview(previewRef.current);
            setPreviewError(undefined);
        }
    }, [isModalOpen]);

    const handlePreview = async () => {
        if (!waterUserId || !month || !year || savingRef.current) return;
        const manual = isManual;
        const { revision } = updatePreview({ type: 'start' });
        const current = () => previewRef.current.revision === revision;
        setPreviewError(undefined);
        const fail = (content: string) => {
            if (!current()) return;
            updatePreview({ type: 'failure', revision });
            setPreviewError(content);
            message.error({ content });
        };
        try {
            const formData = manual ? await form.validateFields() : undefined;
            if (!current()) return;
            const waterUser = await contractRepo.detail(waterUserId);
            if (!current()) return;
            if (!waterUser) {
                fail("Không tìm thấy thông tin khách hàng!");
                return;
            }
            let payload: CreateInvoicePayload;
            let savePayload: CreateInvoicePayload;
            if (manual) {
                payload = { ...formData, month, year, waterFactoryId: waterUser.waterFactoryId, waterUserId: waterUser.id };
                savePayload = payload;
            } else {
                const waterIndexes = await waterIndexRepo.allByWaterUser(waterUser.id);
                if (!current()) return;
                const waterIndex = waterIndexes.find(i => i.month.toString() === month && i.year.toString() === year);
                if (!waterIndex || waterIndex.useAmount == null || waterIndex.oldNumber == null) {
                    fail("Không tìm thấy thông tin chỉ số nước tháng này!");
                    return;
                }
                payload = {
                    month, year, readDate: waterIndex.createdAt, readDateLast: waterUser.lastReadDate,
                    readerUserName: waterIndex.createdBy, waterFactoryId: waterIndex.waterFactoryId,
                    waterMeterNumber: waterIndex.useAmount, waterMeterNumberLast: waterIndex.oldNumber,
                    waterUserId: waterUser.id,
                };
                savePayload = { month, year, waterUserId: waterUser.id, waterIndexId: waterIndex.id };
            }
            const result = await waterInvoiceRepo.previewInvoice(payload);
            if (!current()) return;
            if (!result || typeof result === 'string') {
                fail(typeof result === 'string' ? result : "Xem trước hoá đơn thất bại!");
                return;
            }
            updatePreview({ type: 'success', revision, invoice: result, payload: savePayload, manual });
        } catch (error) {
            if (!current()) return;
            // Ant Design marks invalid fields in place.
            if (error && typeof error === 'object' && 'errorFields' in error) {
                updatePreview({ type: 'failure', revision });
            } else {
                fail(error instanceof Error ? error.message : "Xem trước hoá đơn thất bại!");
            }
        }
    };

    const handleSave = async () => {
        const currentPreview = previewRef.current;
        const payload = getInvoiceSavePayload(currentPreview);
        if (!payload || savingRef.current) return;
        savingRef.current = true;
        setLoading(true);
        try {
            const result = currentPreview.manual
                ? await waterInvoiceRepo.addInvoiceManual(payload)
                : await waterInvoiceRepo.addInvoice(payload);
            resetPreview();
            if (result === true) {
                setIsModalOpen(false);
                message.success({ content: "Tạo hoá đơn thành công!" });
            } else {
                const content = typeof result === 'string' ? result : "Tạo hoá đơn thất bại!";
                setPreviewError(content);
                message.error({ content });
            }
        } catch (error) {
            resetPreview();
            const content = error instanceof Error ? error.message : "Tạo hoá đơn thất bại!";
            setPreviewError(content);
            message.error({ content });
        } finally {
            savingRef.current = false;
            setLoading(false);
        }
    };

    const handleCancel = () => {
        if (savingRef.current) return;
        resetPreview();
        setIsModalOpen(false);
    };
    return <Modal open={isModalOpen} onCancel={handleCancel} width={1200} 
        style={{
            top: 20
        }}
        styles={{
            mask: {
                backdropFilter: 'blur(2px)',
                backgroundColor: 'var(--primary-mask)'
            }
        }}
        footer={[
            <div key={1} className="w-full flex flex-row justify-end px-1.5">
                <Popconfirm
                    title={`Lưu thay đổi?`}
                    description={`Bạn có chắc chắn muốn lưu thay đổi?`}
                    onConfirm={handleSave}
                    okText="Lưu thay đổi"
                    cancelText="Hủy"
                    okButtonProps={{style: {backgroundColor: "var(--primary-color)"}}}
                    icon={<ExclamationCircleFilled style={{color: "var(--danger-color)"}} />}
                >
                    <Tooltip
                        title={(!data||!invoicePayload)?"Bạn cần xem trước trước khi tạo hoá đơn!":null}
                    >
                        <Button key="submit" type="primary" loading={loading}
                            disabled={!data||!invoicePayload||previewLoading||loading}
                            style={{ 
                                background: (!data||!invoicePayload)?"var(--disabled-button)":"var(--primary-color)", 
                                paddingRight: 48, 
                                paddingLeft: 48, 
                                fontSize: 18,
                                fontWeight: 600,
                                paddingTop: 18, 
                                paddingBottom: 18,
                                }}
                        >
                            LƯU
                        </Button>
                    </Tooltip>
                </Popconfirm>
            </div>,
        ]}
    >
        <div className="flex flex-row w-full items-start justify-start select-none">
            <p className="ml-4 text-2xl font-bold text-primary">TẠO HOÁ ĐƠN MỚI</p>
        </div>
        <p className="text-xl font-bold text-primary ml-4 mt-6">1. THÔNG TIN KHÁCH HÀNG</p>
        <ConfigProvider theme={MODAL_THEME}>
            <Form<Partial<CreateInvoicePayload>>
                name="basic"
                className="grid grid-cols-3 gap-2 px-4 pb-2"
                form={form}
                layout="vertical"
                disabled={loading}
                onValuesChange={resetPreview}
            >
                <Form.Item
                    className="w-full col-span-2"
                    label="Hộ dùng nước"
                >
                    <Select
                        showSearch
                        aria-label="Hộ dùng nước"
                        value={waterUserId}
                        onChange={(value) => { resetPreview(); setWaterUserId(value); }}
                        onSearch={(query) => setUserSearchQuery(query)}
                        notFoundContent={null}
                        defaultActiveFirstOption={false}
                        placeholder="Nhập Mã KH, Tên KH, ..."
                        filterOption={false}
                        options={userOptions.map(user => ({
                            label: `${user.code} - ${user.name} - ${user.userName}`,
                            value: user.id
                        }))}
                    />
                </Form.Item>
                <Form.Item
                    label="Tháng"
                    getValueProps={(i) => ({ value: dayjsPipe(i) })}
                >
                    <DatePicker.MonthPicker
                        aria-label="Tháng hóa đơn"
                        format={DEF_MONTH_FORMAT}
                        className="w-full"
                        onChange={(date) => {
                            resetPreview();
                            setMonth(date ? (date.month() + 1).toString() : undefined);
                            setYear(date ? date.year().toString() : undefined);
                        }}
                    />
                </Form.Item>
                <Form.Item
                    className="w-full"
                    label="Kiểu tạo hoá đơn"
                >
                    <Select aria-label="Kiểu tạo hoá đơn" onChange={(value) => { resetPreview(); setManual(value); }} value={isManual}>
                        <Select.Option value={false}>Tự động</Select.Option>
                        <Select.Option value={true}>Thủ công</Select.Option>
                    </Select>   
                </Form.Item>
                {isManual&&<>
                    <Form.Item<Partial<CreateInvoicePayload>>
                        label="Chỉ số đầu"
                        name="waterMeterNumberLast"
                        rules={[{ required: true, message: 'Bạn phải nhập chỉ số đầu!' }]}
                    >
                        <InputNumber className="w-full" placeholder="Nhập tên"></InputNumber>
                    </Form.Item>
                    <Form.Item<Partial<CreateInvoicePayload>>
                        label="Chỉ số cuối"
                        name="waterMeterNumber"
                        rules={[{ required: true, message: 'Bạn phải nhập chỉ số cuối!' }]}
                    >
                        <InputNumber className="w-full" placeholder="Nhập tên"></InputNumber>
                    </Form.Item>
                    <Form.Item<Partial<CreateInvoicePayload>>
                        label="Ngày đọc chỉ số đầu"
                        name="readDateLast"
                        getValueProps={(i) => ({ value: dayjsPipe(i) })}
                    >
                        <DatePicker format={DEF_DATE_FORMAT} className="w-full" placeholder="Chọn ngày đọc chỉ số đầu"></DatePicker>
                    </Form.Item>
                    <Form.Item<Partial<CreateInvoicePayload>>
                        label="Ngày đọc chỉ số cuối"
                        name="readDate"
                        getValueProps={(i) => ({ value: dayjsPipe(i) })}
                    >
                        <DatePicker format={DEF_DATE_FORMAT} className="w-full" placeholder="Chọn ngày đọc chỉ số đầu"></DatePicker>
                    </Form.Item>
                    <Form.Item<Partial<CreateInvoicePayload>>
                        label="Người đọc số nước"
                        name="readerUserName"
                    >
                        <Select showSearch className="w-full" placeholder="Chọn người đọc số nước">
                            {appUsers.map(user => <Select.Option key={user.userName} value={user.userName}>{user.userName} - {user.fullName}</Select.Option>)}
                        </Select>
                    </Form.Item>
                </>}
            </Form>
        </ConfigProvider>
        <div className="mb-3 mx-4 border-t border-dashed border-gray-300"></div>
        <div className="flex flex-row justify-between items-center mr-3">
            <p className="text-xl font-bold text-primary ml-4">2. XEM TRƯỚC HOÁ ĐƠN</p>
            {data&&<Button type="default" icon={<CloseOutlined />} onClick={resetPreview}>Huỷ xem trước</Button>}
        </div>
        {previewError && <Alert className="mx-4 mt-3" type="error" showIcon message={previewError} description="Vui lòng xem trước lại trước khi tạo hóa đơn." />}
        {data && !invoicePayload && <Alert className="mx-4 mt-3" type="warning" showIcon message="Thiếu xác nhận xem trước của giá bán theo hợp đồng. Vui lòng hủy xem trước và thử lại." />}
        <div className="flex flex-col items-center">
            <div className="py-4 px-4 w-full">
                {data
                ?<InvoiceTable data={data} preview></InvoiceTable>
                :<div className="h-[150px] pb-[32px] flex flex-col items-center justify-center">
                    <ConfigProvider theme={MODAL_THEME}>
                        <Tooltip
                            title={(!waterUserId||!month||!year)?"Bạn cần chọn Khách hàng và Tháng tạo hoá đơn!":null}
                        >
                            <Button
                                icon={<EyeOutlined />}
                                onClick={handlePreview}
                                loading={previewLoading}
                                disabled={!waterUserId||!month||!year||loading}
                                type='primary'
                                style={{
                                  background: (!waterUserId||!month||!year)?"var(--disabled-button)":"var(--primary-color)",
                                }}
                            >Xem trước</Button>
                        </Tooltip>
                    </ConfigProvider>
                </div>}
            </div>
        </div>
        <div className="mb-5 mx-4 border-t border-dashed border-gray-300"></div>
    </Modal>
} 